import React, { useContext, useState } from "react";
import axios from "axios";
import { base_url, stripe_key } from "../../config";
import RegistrationContext from "../../contexts/RegistrationContext";
import {
  AlertCircle,
  BadgeCheck,
  Clock,
  CreditCard,
  Info,
  Loader2,
  Trash2,
  TriangleAlert,
  UploadIcon,
} from "lucide-react";
import { loadStripe } from "@stripe/stripe-js";

if (!stripe_key) {
  throw new Error("Stripe key is not defined");
}
const stripePromise = loadStripe(stripe_key);

interface EuraiProofProps {
  ready: (f: string) => void;
}

const EuraiProof: React.FC<EuraiProofProps> = ({ ready }) => {
  const { setError, setData, data } = useContext(RegistrationContext);
  const [fileError, setFileError] = useState("");
  const [fileUploaded, setFileUploaded] = useState(
    data.eurai_proof_file_name || ""
  );

  React.useEffect(() => {
    ready(fileUploaded);
  }, [fileUploaded, ready]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];

      if (selectedFile.size > 5 * 1024 * 1024) {
        setFileError("File size exceeds 5MB limit");
        return;
      }
      if (selectedFile.type !== "application/pdf") {
        setFileError("Only PDF files are accepted");
        return;
      }

      try {
        const formData = new FormData();
        formData.append("file", selectedFile);
        const res = await axios.post(
          `${base_url}/api/update_eurai_proof`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        setFileError("");
        setFileUploaded(selectedFile.name);
        setData({
          ...data,
          eurai_proof_file_name: selectedFile.name,
          cart: res.data?.cart ?? data.cart,
        });
      } catch (error: any) {
        setError(
          "Unexpected error. " + (error.response?.data?.message || error.message)
        );
      }
    }
  };

  const handleFileRemove = async () => {
    setFileError("");
    try {
      const res = await axios.post(`${base_url}/api/remove_eurai_proof`, {});
      setFileUploaded("");
      setData({
        ...data,
        eurai_proof_file_name: "",
        cart: res.data?.cart ?? data.cart,
      });
    } catch (error: any) {
      setError(
        "Unexpected error. " + (error.response?.data?.message || error.message)
      );
    }
  };

  if (fileUploaded) {
    return (
      <li className="flex flex-col border border-gray-300 p-4 rounded-lg mt-8 bg-blue-50">
        <div className="flex items-start mb-2">
          <Info className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
          <span className="font-medium">EurAI Membership Proof Uploaded</span>
        </div>
        <div className="ml-7 space-y-2">
          <p className="text-sm flex items-center space-x-2">
            <span className="text-gray-700">{fileUploaded}</span>
            <button
              onClick={() => handleFileRemove()}
              className="text-red-600 hover:text-red-700"
              aria-label="Remove file"
            >
              <Trash2 className="h-5 w-5" />
            </button>
          </p>
        </div>
      </li>
    );
  }

  return (
    <li className="flex flex-col border border-red-300 p-4 rounded-lg mt-8 bg-red-50">
      <div className="flex items-start mb-2">
        <TriangleAlert className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
        <span className="font-medium">EurAI Membership Proof Required</span>
      </div>
      <div className="ml-7 space-y-2">
        <label htmlFor="eurai-proof-2" className="text-sm font-medium block">
          Upload proof of EurAI membership (PDF, max 5MB)
        </label>
        <input
          id="eurai-proof-2"
          type="file"
          accept=".pdf"
          onChange={handleFileChange}
          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-red-200 file:text-red-700 hover:file:bg-red-300"
        />
        {fileError && <p className="text-sm text-red-500">{fileError}</p>}
      </div>
    </li>
  );
};

export const EurAIStatus: React.FC = () => {
  const { data, setData, setError } = useContext(RegistrationContext);
  const [, setIsProcessingPayment] = useState(false);

  const handleProceedToPayment = async () => {
    setIsProcessingPayment(true);
    try {
      const response = await axios.post(
        `${base_url}/api/create-checkout-session-upgrade-eurai`,
        {}
      );
      const stripe = await stripePromise;
      if (stripe) {
        await stripe.redirectToCheckout({ sessionId: response.data.id });
      } else {
        setError("Stripe initialization failed. Please try again.");
      }
    } catch (error) {
      setError("Failed to initiate payment. Please try again.");
    } finally {
      setIsProcessingPayment(false);
    }
  };

  function EuraiProcessing() {
    return (
      <div className="bg-white rounded-lg shadow border-l-4 border-blue-500 mb-3">
        <div className="flex items-center justify-between p-3">
          <div className="flex items-center">
            <Clock className="h-4 w-4 text-blue-500 mr-2" />
            <h3 className="text-sm font-medium text-gray-800">
              EurAI membership validation
            </h3>
          </div>
          <div className="flex items-center">
            <Loader2 className="h-3 w-3 text-blue-500 animate-spin mr-1" />
            <span className="text-xs font-medium text-blue-500 mr-2">
              Processing
            </span>
          </div>
        </div>
        <div className="p-3 border-t border-gray-100">
          <p className="text-xs text-gray-600 mb-2">
            Your EurAI membership validation is currently being processed. This
            typically takes 2-3 business days.
          </p>
          <div className="w-full bg-gray-200 rounded-full h-1.5 mb-1">
            <div className="bg-blue-500 h-1.5 rounded-full w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  function EuraiOK() {
    return (
      <div className="bg-white rounded-lg shadow border-l-4 border-green-500 mb-3">
        <div className="flex items-center justify-between p-3">
          <div className="flex items-center">
            <BadgeCheck className="h-4 w-4 text-green-500 mr-2" />
            <h3 className="text-sm font-medium text-gray-800">
              Your EurAI membership has been validated
            </h3>
          </div>
        </div>
      </div>
    );
  }

  function EuraiKO({ r = "" }: { r: string }) {
    const [selectedOption, setSelectedOption] = useState<"upload" | "pay">(
      "upload"
    );
    const [fileReady, setFileReady] = useState("");

    const submitNewProofHandler = async () => {
      try {
        await axios.post(`${base_url}/api/eurai_proof_submitted`, {});
        setData({ ...data, eurai_validation: "pending", status: "ok" });
      } catch (error: any) {
        setError(
          "Unexpected error. " +
            (error.response?.data?.message || error.message)
        );
      }
    };

    const disabledButton =
      selectedOption === "upload" && (!fileReady || fileReady === "");

    return (
      <div className="bg-white rounded-lg shadow border-l-4 border-red-500 mb-3">
        <div className="flex-1">
          <div className="mx-auto max-w-3xl">
            <div>
              <div className="flex items-center justify-between pt-3 mb-4">
                <div className="flex items-center">
                  <AlertCircle className="h-4 w-4 text-red-500 mr-2" />
                  <h3 className="text-sm font-medium text-gray-800">
                    EurAI membership validation failed
                  </h3>
                </div>
              </div>

              <div className="mb-4">
                <p className="text-xs text-gray-500">
                  Your EurAI membership verification was unsuccessful. Your
                  registration is currently marked as invalid because the $
                  {data.eurai_upgrade_cost} membership discount was applied.
                  Please resolve this by re-submitting a valid proof or paying
                  the discount difference.
                </p>
              </div>

              <h3 className="mb-4 font-medium">Reason:</h3>
              <p className="rounded-md bg-gray-50 p-3 mb-4 text-gray-700">{r}</p>

              <h3 className="mb-4 font-medium">Available Options:</h3>
              <div className="space-y-4">
                <div
                  className="flex items-start space-x-3 rounded-lg p-4 cursor-pointer"
                  onClick={() => setSelectedOption("upload")}
                >
                  <div className="flex h-5 w-5 items-center justify-center">
                    <input
                      type="radio"
                      id="eurai-upload"
                      name="eurai-option"
                      value="upload"
                      checked={selectedOption === "upload"}
                      onChange={() => setSelectedOption("upload")}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    />
                  </div>
                  <div className="flex-1">
                    <label
                      htmlFor="eurai-upload"
                      className="flex cursor-pointer items-center text-base font-medium"
                    >
                      <UploadIcon className="mr-2 h-5 w-5 text-blue-500" />
                      Upload a new proof of EurAI membership
                    </label>
                    <p className="mt-1 text-sm text-gray-500">
                      Submit a clear, current document that verifies your EurAI
                      membership.
                    </p>
                  </div>
                </div>
                {selectedOption === "upload" && (
                  <div className="ml-12">
                    <EuraiProof ready={setFileReady} />
                  </div>
                )}
                <div
                  className="flex items-start space-x-3 rounded-lg p-4 cursor-pointer"
                  onClick={() => setSelectedOption("pay")}
                >
                  <div className="flex h-5 w-5 items-center justify-center">
                    <input
                      type="radio"
                      id="eurai-pay"
                      name="eurai-option"
                      value="pay"
                      checked={selectedOption === "pay"}
                      onChange={() => setSelectedOption("pay")}
                      className="h-4 w-4 text-green-600 cursor-pointer"
                    />
                  </div>
                  <div className="flex-1">
                    <label
                      htmlFor="eurai-pay"
                      className="flex cursor-pointer items-center text-base font-medium"
                    >
                      <CreditCard className="mr-2 h-5 w-5 text-green-500" />
                      Pay the membership discount difference
                    </label>
                    <p className="mt-1 text-sm text-gray-500">
                      Pay the ${data.eurai_upgrade_cost} EurAI member discount
                      that was applied to your registration to keep your
                      registration at the standard rate.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-4 border-t px-6 py-4">
                <button
                  disabled={disabledButton}
                  className={`rounded-md px-4 py-2 text-sm font-medium text-white ${
                    disabledButton
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700"
                  }`}
                  onClick={
                    selectedOption === "upload"
                      ? () => submitNewProofHandler()
                      : () => handleProceedToPayment()
                  }
                >
                  {selectedOption === "upload"
                    ? "Submit New EurAI Proof"
                    : "Proceed to Payment"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  switch (data.eurai_validation) {
    case "pending":
      return <EuraiProcessing />;
    case "ok":
      return <EuraiOK />;
    case "ko":
      return <EuraiKO r={data.eurai_ko_reason || ""} />;
    default:
      return null;
  }
};
